import React from 'react'
import type { ReputationPayload } from '../../api/reputation'
import { formatRelativeTime, formatScore } from '../../utils/formatNumber'

export default function ReputationModal({
  open,
  onClose,
  reputation,
}: {
  open: boolean
  onClose: () => void
  reputation: ReputationPayload | null
}) {
  const modalRef = React.useRef<HTMLDivElement | null>(null)
  const closeRef = React.useRef<HTMLButtonElement | null>(null)
  const prevFocusRef = React.useRef<HTMLElement | null>(null)

  React.useEffect(() => {
    if (!open || typeof document === 'undefined') return
    prevFocusRef.current = document.activeElement as HTMLElement | null
    const t = setTimeout(() => closeRef.current?.focus(), 0)

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const root = modalRef.current
      if (!root) return
      const focusable = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      clearTimeout(t)
      document.removeEventListener('keydown', onKeyDown)
      prevFocusRef.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  const score = reputation?.score ?? 0
  const bars = [
    {
      label: 'Identity',
      value: reputation?.breakdown?.identity ?? 0,
      cap: 200,
      hint: 'Built from account maturity, verification, and profile completeness.',
    },
    {
      label: 'Contribution',
      value: reputation?.breakdown?.contribution ?? 0,
      cap: 250,
      hint: 'Earned by publishing and engagement over time.',
    },
    {
      label: 'Economic Integrity',
      value: reputation?.breakdown?.economic ?? 0,
      cap: 200,
      hint: 'Earned by reliable transactions with low disputes and chargebacks.',
    },
    {
      label: 'Appreciation (Gifts)',
      value: reputation?.breakdown?.appreciation ?? 0,
      cap: 250,
      hint: 'Earned by unique supporter gifts over time.',
    },
  ]
  const rawPenalty = reputation?.breakdown?.penalty ?? 0
  const penalty = Object.is(rawPenalty, -0) ? 0 : rawPenalty
  const updatedAt = formatRelativeTime(reputation?.last_calculated_at)

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 px-4" data-no-tapback="1">
      <div
        ref={modalRef}
        className="w-full max-w-xl rounded-2xl border border-white/15 bg-slate-950 p-5 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reputation-modal-title"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 id="reputation-modal-title" className="text-lg font-extrabold text-white">Reputation details</h3>
            <div className="text-sm text-white/70">
              {reputation?.tier?.label || 'New Member'} - Score {formatScore(score)}
            </div>
            <div className="text-xs text-white/55">Updated: {updatedAt}</div>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close reputation details"
            className="rounded-md border border-white/15 px-2 py-1 text-xs text-white/80 hover:bg-white/10"
          >
            Close
          </button>
        </div>

        <div className="space-y-3">
          {bars.map((row) => {
            const pct = Math.max(0, Math.min(100, (row.value / row.cap) * 100))
            const empty = row.value === 0
            return (
              <div key={row.label}>
                <div className={`mb-1 flex items-center justify-between text-xs ${empty ? 'text-white/50' : 'text-white/75'}`}>
                  <span>{row.label}</span>
                  <span>{formatScore(row.value)}{empty ? ' - Not yet earned' : ''}</span>
                </div>
                <div className="h-2 rounded-full bg-white/10">
                  <div className={`h-2 rounded-full ${empty ? 'bg-white/20' : 'bg-brand'}`} style={{ width: `${pct}%` }} />
                </div>
                <div className={`mt-1 text-[11px] ${empty ? 'text-white/40' : 'text-white/55'}`}>{row.hint}</div>
              </div>
            )
          })}

          <div>
            <div className={`mb-1 flex items-center justify-between text-xs ${penalty === 0 ? 'text-white/50' : 'text-white/75'}`}>
              <span>Conduct Penalty</span>
              <span>{penalty === 0 ? '0 - Clear' : `-${formatScore(penalty)}`}</span>
            </div>
            <div className="h-2 rounded-full bg-white/10">
              <div
                className={`h-2 rounded-full ${penalty === 0 ? 'bg-white/20' : 'bg-rose-500'}`}
                style={{ width: `${Math.max(0, Math.min(100, (penalty / 300) * 100))}%` }}
              />
            </div>
            <div className={`mt-1 text-[11px] ${penalty === 0 ? 'text-white/40' : 'text-white/55'}`}>
              Only appears if conduct issues occur.
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-white/70">How to improve</div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-white/85">
            {(reputation?.how_to_improve || []).map((line, idx) => (
              <li key={`${line}-${idx}`}>{line}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
